import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { ApiClientConfig } from '../types/api/common';
import { ServiceRegistry } from '../service/ServiceRegistry';
import { HttpClient } from '../service/api/HttpClient';

/**
 * API Context State Interface
 */
interface ApiContextState {
  httpClient: HttpClient;
  isOnline: boolean;
  isInitialized: boolean;
  config: ApiClientConfig;
  updateConfig: (newConfig: Partial<ApiClientConfig>) => void;
  getServiceHealth: () => any[];
  enableMockMode: () => void;
  disableMockMode: () => void;
}

/**
 * API Context Props Interface
 */
interface ApiContextProps {
  children: ReactNode;
  config?: Partial<ApiClientConfig>;
}

/**
 * API Context for managing global API configuration and state
 */
const ApiContext = createContext<ApiContextState | undefined>(undefined);

/**
 * API Context Provider Component
 * 
 * Features:
 * - HTTP client management
 * - Service registry integration
 * - Network status monitoring
 * - Configuration management
 * - Health monitoring
 * - Mock mode support
 */
export function ApiProvider({ children, config: initialConfig }: ApiContextProps) {
  const [httpClient] = useState(() => HttpClient.getInstance(initialConfig));
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);
  const [config, setConfig] = useState<ApiClientConfig>(httpClient['config']);

  /**
   * Update API configuration
   */
  const updateConfig = (newConfig: Partial<ApiClientConfig>) => {
    httpClient.updateConfig(newConfig);
    setConfig(httpClient['config']);
    ServiceRegistry.initialize(); // Reinitialize services with new config
  };

  /**
   * Get health status of all services
   */
  const getServiceHealth = () => {
    return ServiceRegistry.getHealthStatus();
  };

  /**
   * Enable mock mode for testing
   */
  const enableMockMode = () => {
    ServiceRegistry.enableMockMode();
  };

  /**
   * Disable mock mode
   */
  const disableMockMode = () => {
    ServiceRegistry.disableMockMode();
  };

  /**
   * Handle online/offline status changes
   */
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Initialize API services
   */
  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Initialize service registry
        ServiceRegistry.initialize();
        
        // Set up global error handlers
        window.addEventListener('unhandledrejection', (event) => {
          console.error('Unhandled API promise rejection:', event.reason);
          // Could send to error reporting service
        });

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize API services:', error);
        setIsInitialized(false);
      }
    };

    initializeServices();
  }, []);

  /**
   * Handle network status for API requests
   */
  useEffect(() => {
    if (!isOnline) {
      console.warn('Application is offline. Some features may not work.');
      // Could show offline banner or notification
    } else {
      console.info('Application is back online.');
      // Could retry failed requests
    }
  }, [isOnline]);

  const contextValue: ApiContextState = {
    httpClient,
    isOnline,
    isInitialized,
    config,
    updateConfig,
    getServiceHealth,
    enableMockMode,
    disableMockMode,
  };

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
}

/**
 * Hook to use API context
 */
export function useApiContext(): ApiContextState {
  const context = useContext(ApiContext);
  
  if (context === undefined) {
    throw new Error('useApiContext must be used within an ApiProvider');
  }
  
  return context;
}

/**
 * Hook to check if API is ready
 */
export function useApiReady(): boolean {
  const { isInitialized, isOnline } = useApiContext();
  return isInitialized && isOnline;
}

/**
 * Hook to get network status
 */
export function useNetworkStatus(): {
  isOnline: boolean;
  isOffline: boolean;
} {
  const { isOnline } = useApiContext();
  
  return {
    isOnline,
    isOffline: !isOnline,
  };
}

/**
 * Hook for API health monitoring
 */
export function useApiHealth(): {
  healthStatus: any[];
  isHealthy: boolean;
  refresh: () => void;
} {
  const { getServiceHealth } = useApiContext();
  const [healthStatus, setHealthStatus] = useState<any[]>([]);

  const refresh = () => {
    const status = getServiceHealth();
    setHealthStatus(status);
  };

  useEffect(() => {
    refresh();
    
    // Refresh health status every 30 seconds
    const interval = setInterval(refresh, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const isHealthy = healthStatus.every(service => service.status === 'healthy');

  return {
    healthStatus,
    isHealthy,
    refresh,
  };
}
